import type { FC, ReactNode, SelectHTMLAttributes } from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label: string
    hideLabel?: boolean
    options: Array<{ value: string, label: string }>
    wrapperClassName?: string
}

/** A styled native select, so it works with every keyboard, screen reader and phone. */
export const Select: FC<SelectProps> = ({ label, hideLabel = true, options, wrapperClassName, className, id, ...props }): ReactNode => {
    const selectId = id ?? `select-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

    return (
        <div className={cn('relative', wrapperClassName)}>
            <label htmlFor={selectId} className={hideLabel ? 'sr-only' : 'mb-2 block text-xs font-medium text-muted-foreground'}>
                {label}
            </label>

            <select
                id={selectId}
                className={cn(
                    'h-11 w-full cursor-pointer appearance-none rounded-full border border-input bg-transparent pl-4 pr-10 text-sm text-foreground transition hover:border-ivory/30 focus:border-gold/60 focus:outline-none',
                    className
                )}
                {...props}
            >
                {options.map(option => (
                    <option key={option.value} value={option.value} className='bg-[#141414] text-ivory'>
                        {option.label}
                    </option>
                ))}
            </select>

            <ChevronDown className={cn('pointer-events-none absolute right-4 size-4 text-muted-foreground', hideLabel ? 'top-3.5' : 'bottom-3.5')} aria-hidden='true' />
        </div>
    )
}
